import { useState, useMemo } from "react";

// JsonViewer renders structured JSON as a navigable, collapsible tree.
// Designed for the admin trace expand-panel where operators inspect
// per-attempt packets / responses / parsed outputs — these can be deep
// nested objects (V2 ANALYZE inputs hit 30+ top-level keys, with
// arrays-of-objects nested 4 levels deep).
//
// Capabilities:
//   - Recursive render of objects + arrays with expand/collapse toggles
//   - Auto-collapse below `defaultExpandDepth` so the initial render is
//     scannable (operator opens what they care about)
//   - Click-to-copy each key's path (Ctrl/Cmd-click copies the value
//     instead) — useful when raising bugs that reference a specific
//     nested field
//   - Inline string-search filter (when a query is set, branches whose
//     subtree contains a match expand automatically; non-matching
//     siblings collapse so the tree zooms to relevant nodes)
//   - Long-string truncation with "show more"
//
// No third-party dep — matches the rest of the admin UI's no-render-lib
// discipline.

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

const MAX_INLINE_STRING = 200;

export function JsonViewer({
  value,
  defaultExpandDepth = 2,
  showSearch = true,
}: {
  value: unknown;
  defaultExpandDepth?: number;
  showSearch?: boolean;
}) {
  const [query, setQuery] = useState("");
  const normalized = useMemo(() => normalizeForView(value), [value]);
  const queryLower = query.toLowerCase();

  if (normalized === undefined) {
    return <span className="text-gray-500 italic">(empty)</span>;
  }

  return (
    <div>
      {showSearch && (
        <div className="mb-2 flex items-center gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="filter (auto-expands matches)"
            className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs font-mono text-gray-100 placeholder:text-gray-500"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-xs text-gray-400 hover:text-gray-200"
            >
              clear
            </button>
          )}
        </div>
      )}
      <div className="font-mono text-xs leading-snug">
        <Node
          value={normalized}
          path=""
          depth={0}
          defaultExpandDepth={defaultExpandDepth}
          query={queryLower}
          isRoot
        />
      </div>
    </div>
  );
}

// normalizeForView accepts the AttemptTrace.parsed_output (typed as
// `unknown`) and returns either undefined (empty / null) or a JsonValue
// that the recursive Node component can render. Filters BSON/Firestore
// shapes if any sneak through (we ParsedOutput as `any` server-side to
// dodge ID-as-byte-array drift; this normalizes to the operator-facing
// shape).
function normalizeForView(v: unknown): JsonValue | undefined {
  if (v === null || v === undefined) return undefined;
  return v as JsonValue;
}

function Node({
  value,
  path,
  depth,
  defaultExpandDepth,
  query,
  keyLabel,
  isRoot,
}: {
  value: JsonValue;
  path: string;
  depth: number;
  defaultExpandDepth: number;
  query: string;
  keyLabel?: string;
  isRoot?: boolean;
}) {
  const hasQuery = query !== "";
  const matchesQuery = useMemo(
    () => (hasQuery ? subtreeMatches(value, query, keyLabel) : false),
    [value, query, keyLabel, hasQuery],
  );

  // Expansion logic:
  //   - When a query is active: auto-expand any node whose subtree matches
  //   - Otherwise: respect manual toggle, default to expanded up to
  //     `defaultExpandDepth`
  const [manuallyToggled, setManuallyToggled] = useState<boolean | null>(null);
  const autoExpanded = hasQuery ? matchesQuery : depth < defaultExpandDepth;
  const expanded = manuallyToggled ?? autoExpanded;

  // Skip rendering branches that don't match the query and have no
  // descendants matching either. Always render root so the empty-state
  // is informative.
  if (hasQuery && !matchesQuery && !isRoot) {
    return null;
  }

  if (typeof value !== "object" || value === null) {
    return (
      <LeafNode value={value} keyLabel={keyLabel} path={path} query={query} />
    );
  }

  const isArray = Array.isArray(value);
  const entries: [string | number, JsonValue][] = isArray
    ? value.map((v, i) => [i, v])
    : Object.entries(value);

  const open = isArray ? "[" : "{";
  const close = isArray ? "]" : "}";
  const summary = isArray
    ? `${entries.length} item${entries.length === 1 ? "" : "s"}`
    : `${entries.length} key${entries.length === 1 ? "" : "s"}`;

  return (
    <div className={isRoot ? "" : "pl-3 border-l border-gray-800"}>
      <button
        onClick={() => setManuallyToggled(!expanded)}
        className="text-left hover:bg-gray-800 px-1 -ml-1 rounded"
      >
        <span className="text-gray-500">{expanded ? "▼" : "▶"}</span>{" "}
        {keyLabel !== undefined && (
          <>
            <span className="text-sky-300">{renderKey(keyLabel)}</span>
            <span className="text-gray-500">: </span>
          </>
        )}
        <span className="text-gray-400">
          {open}
          {!expanded && ` ${summary} `}
          {!expanded && close}
        </span>
      </button>
      {expanded && (
        <div>
          {entries.map(([k, v]) => (
            <Node
              key={String(k)}
              value={v}
              path={path === "" ? String(k) : `${path}.${k}`}
              depth={depth + 1}
              defaultExpandDepth={defaultExpandDepth}
              query={query}
              keyLabel={String(k)}
            />
          ))}
          <div className="text-gray-400 -ml-1">{close}</div>
        </div>
      )}
    </div>
  );
}

function LeafNode({
  value,
  keyLabel,
  path,
  query,
}: {
  value: string | number | boolean | null;
  keyLabel?: string;
  path: string;
  query: string;
}) {
  const [expanded, setExpanded] = useState(false);

  const valueStr =
    value === null
      ? "null"
      : typeof value === "string"
        ? value
        : String(value);
  const needsTruncation =
    typeof value === "string" && value.length > MAX_INLINE_STRING;
  const displayStr =
    needsTruncation && !expanded
      ? valueStr.slice(0, MAX_INLINE_STRING) + "…"
      : valueStr;
  const className =
    value === null
      ? "text-gray-500"
      : typeof value === "string"
        ? "text-emerald-200"
        : typeof value === "number"
          ? "text-amber-200"
          : typeof value === "boolean"
            ? "text-purple-300"
            : "";
  const quote = typeof value === "string" ? `"` : "";

  return (
    <div className="pl-3 border-l border-gray-800">
      <span className="px-1">
        {keyLabel !== undefined && (
          <>
            <span className={highlightIfMatch(keyLabel, query, "text-sky-300")}>
              {renderKey(keyLabel)}
            </span>
            <span className="text-gray-500">: </span>
          </>
        )}
        <span className={highlightIfMatch(valueStr, query, className)}>
          {quote}
          {displayStr}
          {quote}
        </span>
        {needsTruncation && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-2 text-[10px] text-gray-500 hover:text-gray-300"
          >
            [{expanded ? "collapse" : `show ${value.length - MAX_INLINE_STRING} more chars`}]
          </button>
        )}
        <button
          onClick={() => void navigator.clipboard.writeText(path)}
          title={`Copy path: ${path}`}
          className="ml-2 text-[10px] text-gray-600 hover:text-gray-300"
        >
          ⧉
        </button>
      </span>
    </div>
  );
}

function renderKey(k: string | number) {
  // Numeric keys (array indices) render bracketless; string keys quoted.
  if (typeof k === "number" || (typeof k === "string" && /^\d+$/.test(k))) {
    return `[${k}]`;
  }
  return `"${k}"`;
}

function highlightIfMatch(s: string, query: string, base: string): string {
  if (query !== "" && s.toLowerCase().includes(query)) {
    return `${base} bg-yellow-900/40`;
  }
  return base;
}

// subtreeMatches: does any key or scalar value at-or-below `value`
// contain the query as a substring? Used to decide whether to render +
// auto-expand a node when a search query is active.
function subtreeMatches(
  value: JsonValue,
  query: string,
  keyLabel?: string,
): boolean {
  if (keyLabel !== undefined && keyLabel.toLowerCase().includes(query)) {
    return true;
  }
  if (typeof value === "string") {
    return value.toLowerCase().includes(query);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value).toLowerCase().includes(query);
  }
  if (value === null) {
    return "null".includes(query);
  }
  if (Array.isArray(value)) {
    return value.some((v) => subtreeMatches(v, query, undefined));
  }
  return Object.entries(value).some(([k, v]) => subtreeMatches(v, query, k));
}
